import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, ShoppingCart, Package, Check } from 'lucide-react';
import { useUserRole, UserRole } from '@/hooks/useUserRole';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { DropdownMenuSeparator, DropdownMenuItem } from '@/components/ui/dropdown-menu';

interface PortalOption {
  role: 'admin' | 'buyer' | 'supplier';
  label: string;
  icon: React.ReactNode;
  path: string;
}

export function PortalSwitcherMenu() {
  const navigate = useNavigate();
  const location = useLocation();
  const { role: currentRole, allRoles, switchRole } = useUserRole();
  const { hasRole } = useAuth();

  // Only show for admins - admins can access ALL portals regardless of their database roles
  if (!hasRole('admin')) return null;

  const portals: PortalOption[] = [
    {
      role: 'admin',
      label: 'Admin Portal',
      icon: <Shield className="size-4" />,
      path: '/admin/dashboard'
    },
    {
      role: 'buyer',
      label: 'Buyer Portal',
      icon: <ShoppingCart className="size-4" />,
      path: '/buyer/dashboard'
    },
    {
      role: 'supplier',
      label: 'Supplier Portal',
      icon: <Package className="size-4" />,
      path: '/supplier/dashboard'
    }
  ];

  const isCurrentPortal = (portalRole: string) => {
    // Check current URL to determine active portal
    const currentPath = location.pathname;
    if (currentPath.startsWith('/admin')) return portalRole === 'admin';
    if (currentPath.startsWith('/buyer')) return portalRole === 'buyer';
    if (currentPath.startsWith('/supplier')) return portalRole === 'supplier';
    return false;
  };

  const handlePortalSwitch = async (portal: PortalOption) => {
    // Admins can access all portals via ProtectedRoute bypass
    // No need to check allRoles - admins always have access
    
    // Don't switch if already on this portal
    if (isCurrentPortal(portal.role)) {
      return;
    }

    // Switch role in state (for UX purposes)
    // Even if admin doesn't have buyer/supplier role in DB, they can still switch for navigation
    switchRole(portal.role);
    toast.success(`Switched to ${portal.label}`);
    
    // Navigate to portal dashboard
    setTimeout(() => {
      window.location.href = portal.path;
    }, 100);
  };

  // For admins, always show all portals (they have access to all via ProtectedRoute bypass)
  // Don't filter based on allRoles - admins can access everything
  const availablePortals = portals;

  return (
    <>
      <DropdownMenuSeparator className="my-1" />
      <div className="px-2 py-1.5">
        <p className="px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Portal Access
        </p>
      </div>
      {availablePortals.map((portal) => {
        const isActive = isCurrentPortal(portal.role);
        return (
          <DropdownMenuItem
            key={portal.role}
            onClick={() => handlePortalSwitch(portal)}
            className={cn(
              "cursor-pointer px-3 py-2 gap-2",
              isActive
                ? "bg-info/5 text-info font-medium hover:bg-info/10 focus:bg-info/10"
                : "focus:bg-muted/70 focus:text-foreground hover:bg-muted/70 hover:text-foreground"
            )}
          >
            <span className={cn("flex-shrink-0", isActive ? "text-info" : "text-muted-foreground")}>
              {portal.icon}
            </span>
            <span className="flex-1">{portal.label}</span>
            {isActive && (
              <Check className="size-4 shrink-0 text-info" />
            )}
          </DropdownMenuItem>
        );
      })}
    </>
  );
}

